import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import StarRating from '../components/StarRating';
import { 
  ArrowLeft, Edit, Trash2, FileText, MapPin, Phone, Mail,
  ChevronLeft, ChevronRight, User, CheckCircle, Shield, AlertCircle,
  Tag, Clock, DollarSign, MessageSquare
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getImageUrl, getFirstImage } from '../utils/imageHelper';
import ListingImage from '../components/ListingImage';

const ClassifiedDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [classified, setClassified] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [deleting, setDeleting] = React.useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  
  React.useEffect(() => {
    const fetchClassified = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = sessionStorage.getItem('token');
        
        const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setClassified(data);
        } else {
          navigate('/listings/classifieds');
        }
      } catch (error) {
        console.error('Error:', error);
        navigate('/listings/classifieds');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) fetchClassified();
  }, [id, navigate]);
  
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this classified ad?')) return;
    
    setDeleting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Classified deleted successfully' });
        navigate('/listings/classifieds');
      } else {
        toast({ title: 'Error', description: 'Failed to delete classified', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error deleting classified', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const nextImage = () => {
    if (classified?.media?.length > 0) {
      setSelectedImage((prev) => (prev + 1) % classified.media.length);
    }
  };

  const prevImage = () => {
    if (classified?.media?.length > 0) {
      setSelectedImage((prev) => (prev - 1 + classified.media.length) % classified.media.length);
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }
  
  if (!classified) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Classified not found</div>
        </div>
      </DashboardLayout>
    );
  }
  
  const data = classified.data || {};
  const images = classified.media || [];
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 px-4">
          <Button variant="ghost" onClick={() => navigate('/listings/classifieds')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Classifieds
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/listings/classifieds/edit/${id}`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="h-4 w-4 mr-2" />
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4">
          
          {/* LEFT COLUMN - Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative bg-white rounded-xl overflow-hidden border aspect-square">
              {images.length > 0 ? (
                <>
                  <ListingImage src={images[selectedImage]} alt={classified.title} className="w-full h-full object-contain" showPlaceholder={false} />
                  {images.length > 1 && (
                    <>
                      <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <FileText className="h-24 w-24 text-gray-300" />
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((url, index) => (
                  <button
                    key={url.id || url.name || `${url.title || "url"}-${index}`}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${selectedImage === index ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <ListingImage src={url} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1">Share</Button>
              <Button variant="outline" className="flex-1">Save</Button>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
              </TabsList>
              
              <TabsContent value="description" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{classified.description}</p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="details" className="mt-4">
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    {data.condition && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Condition</span>
                        <span className="font-medium capitalize">{data.condition}</span>
                      </div>
                    )}
                    {data.brand && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Brand</span>
                        <span className="font-medium">{data.brand}</span>
                      </div>
                    )}
                    {data.model && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Model</span>
                        <span className="font-medium">{data.model}</span>
                      </div>
                    )}
                    {data.yearOfPurchase && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Year of Purchase</span>
                        <span className="font-medium">{data.yearOfPurchase}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Posted On</span>
                      <span className="font-medium">{new Date(classified.created_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* RIGHT COLUMN - Info */}
          <div className="space-y-6">
            {/* Title & Status */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={classified.status === 'active' ? 'default' : 'secondary'}>{classified.status}</Badge>
                {data.adType && <Badge variant="outline" className="capitalize">{data.adType}</Badge>}
                {data.negotiable && <Badge className="bg-orange-500">Negotiable</Badge>}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{classified.title}</h1>
              <div className="mt-2">
                <StarRating listingId={id} size="md" showCount={true} fetchData={true} />
              </div>
            </div>

            {/* Seller Info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <User className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Posted by</p>
                <p className="font-medium">{data.contactName || classified.vendor_name || 'Seller'}</p>
              </div>
            </div>

            {/* Category */}
            <div className="flex gap-2 flex-wrap">
              {classified.category && <Badge variant="outline">{classified.category}</Badge>}
              {classified.subcategory && <Badge variant="outline">{classified.subcategory}</Badge>}
            </div>

            {/* Pricing */}
            {data.price && (
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-gray-900">₹{parseFloat(data.price).toLocaleString()}</span>
                  {data.negotiable && <span className="text-sm text-orange-600">(Negotiable)</span>}
                </div>
                {data.condition && (
                  <p className="text-sm text-gray-500 mt-1 capitalize">Condition: {data.condition}</p>
                )}
              </div>
            )}

            {/* Location */}
            {(data.location || data.city || data.area) && (
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium">Location</h4>
                      <p className="text-sm text-gray-600">
                        {[data.area, data.city, data.location].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Information */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {data.contactPhone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-gray-400" />
                    <span className="font-medium">{data.contactPhone}</span>
                  </div>
                )}
                {data.contactEmail && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <span className="font-medium">{data.contactEmail}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button className="w-full h-12 text-lg bg-gray-900 hover:bg-gray-800" disabled>
                <Phone className="h-5 w-5 mr-2" />
                CONTACT SELLER
              </Button>
              <Button variant="outline" className="w-full h-12 text-lg" disabled>
                <MessageSquare className="h-5 w-5 mr-2" />
                SEND MESSAGE
              </Button>
              <p className="text-xs text-center text-gray-500">(Preview mode - Buttons disabled in admin view)</p>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center">
                <CheckCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Verified</p>
              </div>
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Secure</p>
              </div>
              <div className="text-center">
                <AlertCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Support</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ClassifiedDetails;